import { useEffect } from 'react';
import { Navigate } from 'react-router';
import { HomePage } from '@/pages/HomePage';
import { useAuth } from './use-auth';
import { useAuthDialog } from './use-auth-dialog';
import type { AuthMode } from './auth-dialog-context';

/**
 * The addressable form of the sign-in dialog.
 *
 * Signing in is normally client state — a button opens the dialog and the page
 * behind it never moves. But `/login` and `/register` still have to be real
 * addresses: they are bookmarked, pasted into chat, and printed in old links,
 * and a 404 for any of those would be worse than the redirect it replaced.
 *
 * So the URL renders the HOME page as its backdrop and opens the dialog over it.
 * Closing goes to `/`, because a bare `/login` with the dialog dismissed would be
 * a blank dead end.
 *
 * There is deliberately no `?next=`. Nothing produces one any more — RequireAuth
 * prompts in place rather than redirecting — so every value reaching here would
 * be bookmark- or attacker-supplied, and `next=//evil.example` is a redirect this
 * app has no reason to be able to perform.
 */
export function AuthRoute({ mode }: { mode: AuthMode }) {
  const { open } = useAuthDialog();
  const { isAuthenticated, isLoading } = useAuth();

  // Gated on the probe, like RootLayout and RequireAuth. Without it a signed-in
  // visitor opening a stale bookmark gets a sign-in form thrown over the home
  // page with focus stolen into it.
  const shouldOpen = !isLoading && !isAuthenticated;

  useEffect(() => {
    if (shouldOpen) {
      open(mode, { closeTo: '/' });
    }
  }, [shouldOpen, open, mode]);

  // Already signed in: the address has nothing left to say. Replaced rather than
  // pushed, so Back does not bounce straight into it again.
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <HomePage />;
}
